import { useCallback, useEffect, useRef } from 'react';
import { useTimePickerShorthandStore } from '@/store/TimePickerShorthandStore';
import { useUserPreferences } from '@/common/application/hooks/userPreferencesHook';

// Global keyboard shortcut handler - registered only once
let keyboardShortcutRegistered = false;

/**
 * Hook to access and manage TimePicker shorthand state.
 * Integrates Zustand store with user preferences for persistence.
 * Handles Ctrl+Space (or Cmd+Space on Mac) keyboard shortcut to toggle shorthand mode.
 */
export const useTimePickerShorthand = () => {
  const { preferences, setPreference } = useUserPreferences();
  const isShorthandEnabled = useTimePickerShorthandStore((state) => state.isShorthandEnabled);
  const setShorthandEnabled = useTimePickerShorthandStore((state) => state.setShorthandEnabled);
  const setPreferenceRef = useRef(setPreference);
  const lastSyncedPreferenceRef = useRef<boolean | undefined>(undefined);
  setPreferenceRef.current = setPreference;

  // Sync store with user preferences on mount and when preferences change
  // Use ref to track last synced value to prevent infinite loops
  useEffect(() => {
    const preferenceValue = preferences.timePickerShorthandEnabled ?? false;
    // Only sync if preference actually changed (not just store state)
    if (lastSyncedPreferenceRef.current !== preferenceValue) {
      lastSyncedPreferenceRef.current = preferenceValue;
      const currentStoreValue = useTimePickerShorthandStore.getState().isShorthandEnabled;
      if (currentStoreValue !== preferenceValue) {
        setShorthandEnabled(preferenceValue);
      }
    }
  }, [preferences.timePickerShorthandEnabled, setShorthandEnabled]);

  // Sync user preferences when store changes
  const handleToggleShorthand = useCallback(() => {
    const store = useTimePickerShorthandStore.getState();
    const newValue = !store.isShorthandEnabled;
    setShorthandEnabled(newValue);
    lastSyncedPreferenceRef.current = newValue; // Update ref to prevent sync loop
    setPreferenceRef.current('timePickerShorthandEnabled', newValue);
  }, [setShorthandEnabled]);

  // Global keyboard shortcut handler (works anywhere in the app) - register only once
  useEffect(() => {
    if (keyboardShortcutRegistered) {
      return;
    }

    keyboardShortcutRegistered = true;

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Space (or Cmd+Space on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === ' ') {
        // Don't prevent default if user is typing in an input/textarea
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
          // Only toggle if not in a content-editable or input field (unless it's a TimePicker)
          // Check if it's within a TimePicker by looking for the container
          const timePickerContainer = target.closest('[data-timepicker-container]');
          if (!timePickerContainer) {
            return; // Don't toggle if typing in a regular input
          }
        }
        event.preventDefault();
        const store = useTimePickerShorthandStore.getState();
        const newValue = !store.isShorthandEnabled;
        store.setShorthandEnabled(newValue);
        lastSyncedPreferenceRef.current = newValue; // Update ref to prevent sync loop
        setPreferenceRef.current('timePickerShorthandEnabled', newValue);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      keyboardShortcutRegistered = false;
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  return {
    isShorthandEnabled,
    toggleShorthand: handleToggleShorthand
  };
};
